/**
 * Module dependencies.
 */

import { AdvisorContext, Filter, LastAction } from './context';
import { Choice, Step, StepKey } from 'src/types/advisor';
import { ModelRangeProps } from 'src/types/model-ranges';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { advisorFiltersKeys, useAdvisorSteps } from 'src/core/advisor-config';
import { stepsKeys } from 'src/core/advisor-config/steps-options';
import concat from 'lodash/concat';
import dropRight from 'lodash/dropRight';
import filter from 'lodash/filter';
import findIndex from 'lodash/findIndex';
import head from 'lodash/head';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import size from 'lodash/size';
import slice from 'lodash/slice';
import useAllModelRanges from 'src/api/model-ranges/use-all-model-ranges';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
};

/**
 * Get filter.
 */

function getFilter(filters: Filter[], key: string) {
  return filter(filters, filterObject => {
    if (
      key === advisorFiltersKeys.butane ||
      key === advisorFiltersKeys.propane
    ) {
      return (
        Object.keys(filterObject)[0] === advisorFiltersKeys.butane ||
        Object.keys(filterObject)[0] === advisorFiltersKeys.propane
      );
    }

    return Object.keys(filterObject)[0] === key;
  });
}

/**
 * Get index of filter.
 */

function getIndexOfFilter(submittedFilters: string[], key: string) {
  return findIndex(submittedFilters, (filter: string) => {
    if (
      key === advisorFiltersKeys.butane ||
      key === advisorFiltersKeys.propane
    ) {
      return (
        filter === advisorFiltersKeys.butane ||
        filter === advisorFiltersKeys.propane
      );
    }

    return filter === key;
  });
}

/**
 * Get filter key of step.
 */

function getStepFilterKey(step: Step) {
  if (!step) {
    return advisorFiltersKeys.market;
  }

  if (step.nextStep.step.pageKey === stepsKeys.gasContentSlider) {
    return advisorFiltersKeys.butane;
  }

  return step.nextStep.step.filterKey;
}

/**
 * `AdvisorProvider` provider.
 */

const AdvisorProvider = ({ children }: Props) => {
  const [visibleStep, setVisibleStep] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<Step | null>(null);
  const steps = useAdvisorSteps();
  const [choices, setChoices] = useState<Choice[]>([
    {
      pageKey: stepsKeys.typeGas,
      value: null
    }
  ]);

  const [filledSteps, setFilledSteps] = useState<Step[]>([]);
  const [filters, setFilters] = useState<Filter[]>([]);
  const [filtersHistory, setFiltersHistory] = useState<Filter[]>([]);
  const [submittedFilters, setSubmittedFilters] = useState<string[]>([]);
  const [lastAction, setLastAction] = useState<LastAction>('initial');
  const [initialModelRanges, setInitialModelRanges] = useState<
    ModelRangeProps[] | null
  >(null);

  const normalizedFilters = useMemo(() => {
    const normalizedFilters = {};

    map(filters, filter => {
      normalizedFilters[Object.keys(filter)[0]] = Object.values(filter)[0];
    });

    return normalizedFilters;
  }, [filters]);

  const {
    data: filteredModelRanges,
    isLoading: isLoadingModelRanges,
    isSuccess: isSuccessModelRanges,
    refetch
  } = useAllModelRanges({
    filterParams: normalizedFilters
  });

  const getNextStep = useCallback(
    (value?: string) => {
      if (!currentStep) {
        return steps[value];
      }

      if (currentStep?.nextStep?.options?.default) {
        return currentStep.nextStep.options.default;
      }

      if (currentStep?.nextStep?.options[value]) {
        return currentStep.nextStep.options[value];
      }

      return null;
    },
    [currentStep, steps]
  );

  const handleSetFilter = useCallback((key: string, value: string) => {
    const newFilter = {
      [key]: JSON.parse(value)
    };

    setFilters(previous => {
      if (isEmpty(getFilter(previous, key))) {
        setFiltersHistory(concat(previous, newFilter));

        return concat(previous, newFilter);
      }

      const newFilters = map(previous, filter => {
        if (Object.keys(filter)[0] === key) {
          return newFilter;
        }

        return filter;
      });

      setFiltersHistory(newFilters);

      return newFilters;
    });

    setSubmittedFilters(previous => {
      if (includes(previous, key)) {
        const lastIndex = previous.indexOf(key);

        if (lastIndex >= 0) {
          previous = slice(previous, 0, lastIndex);
        }
      }

      return concat(previous, key);
    });
  }, []);

  const handleRemoveFilter = useCallback(
    (nextStep: Step) => {
      const filterKey = getStepFilterKey(nextStep);

      if (!isEmpty(submittedFilters)) {
        const lastIndex = filterKey
          ? getIndexOfFilter(submittedFilters, filterKey)
          : -1;

        setFilters(previous => {
          if (lastIndex >= 0) {
            return lastIndex === 0
              ? []
              : slice(previous, 0, filterKey ? lastIndex : lastIndex + 1);
          }

          return dropRight(previous);
        });
      }
    },
    [submittedFilters]
  );

  const handleAddFilter = useCallback(
    (nextStep: Step) => {
      const filterKey = getStepFilterKey(nextStep);

      if (!isEmpty(submittedFilters)) {
        const lastIndex = filterKey
          ? getIndexOfFilter(submittedFilters, filterKey)
          : nextStep.nextStep.step.pageIndex;

        setFilters(() => {
          return slice(filtersHistory, 0, lastIndex);
        });
      }
    },
    [filtersHistory, submittedFilters]
  );

  const handleUpdateChoices = useCallback((choice: Choice, index) => {
    setChoices(previous => {
      previous[index] = choice;

      return slice(previous, 0, index + 1);
    });
  }, []);

  const choiceAlreadyExists = useCallback(
    (nextChoiceKey: StepKey) => {
      return !isEmpty(
        filter(choices, ({ pageKey }) => {
          return pageKey === nextChoiceKey;
        })
      );
    },
    [choices]
  );

  const handleSetNextChoice = useCallback(
    (nextChoiceKey: StepKey) => {
      setChoices(previous => {
        if (!choiceAlreadyExists(nextChoiceKey)) {
          return concat(previous, {
            pageKey: nextChoiceKey,
            value: null
          });
        }

        const choiceIndex = findIndex(previous, ['pageKey', nextChoiceKey]);

        if (choiceIndex < 0) {
          previous = concat(previous, {
            pageKey: nextChoiceKey,
            value: null
          });
        }

        if (choiceIndex >= 0) {
          previous[choiceIndex].value = null;
        }

        return previous;
      });
    },
    [choiceAlreadyExists]
  );

  const handleUpdateFilledSteps = useCallback((step: Step, index) => {
    setFilledSteps(previous => {
      if (size(previous) >= index) {
        previous[index] = step;

        return previous;
      }

      return concat(previous, step);
    });
  }, []);

  const getCheckedValue = useCallback(
    (pageKey: StepKey, value?: string) => {
      const matchingValues = filter(choices, choice => {
        if (choice.pageKey !== pageKey) {
          return false;
        }

        return !value ? choice.value !== null : choice.value === value;
      });

      const firstValue =
        !isEmpty(matchingValues) && head(matchingValues)?.value;

      return firstValue ? String(firstValue) : null;
    },
    [choices]
  );

  const handleGoToPrevious = useCallback(
    (currentIndex: number) => {
      const index = currentIndex > 1 ? currentIndex - 2 : -1;
      const previousStep = index >= 0 ? filledSteps[index] : null;

      setLastAction('previous');
      setVisibleStep(index);
      setCurrentStep(previousStep);
      refetch();
      handleRemoveFilter(previousStep);
    },
    [filledSteps, handleRemoveFilter, refetch]
  );

  const handleGoToStep = useCallback(
    (currentIndex: number) => {
      const currentVisibleStep = visibleStep;
      const nextStep =
        currentIndex === 0 ? null : filledSteps[currentIndex - 1];

      setVisibleStep(currentIndex);
      setCurrentStep(nextStep);
      refetch();

      if (currentIndex > currentVisibleStep) {
        handleAddFilter(nextStep);
        setLastAction('previous');

        return;
      }

      handleRemoveFilter(nextStep);
      setLastAction('next');
    },
    [filledSteps, handleAddFilter, handleRemoveFilter, refetch, visibleStep]
  );

  const handleGoToNext = useCallback(
    (value, pageKey, pageIndex) => {
      setLastAction('next');
      setVisibleStep(pageIndex + 1);

      const step = getNextStep(value);
      const isLastStep = step?.nextStep.step.isLastStep;

      handleUpdateChoices({ pageKey, value }, pageIndex);

      if (!isLastStep) {
        handleSetNextChoice(step.nextStep.step.pageKey);
      }

      handleUpdateFilledSteps(step, pageIndex);
      setCurrentStep(step);
    },
    [
      getNextStep,
      handleSetNextChoice,
      handleUpdateChoices,
      handleUpdateFilledSteps
    ]
  );

  useEffect(() => {
    if (!initialModelRanges && isSuccessModelRanges) {
      setInitialModelRanges(filteredModelRanges);
    }
  }, [filteredModelRanges, initialModelRanges, isSuccessModelRanges]);

  return (
    <AdvisorContext.Provider
      value={{
        choices,
        currentStep,
        filteredModelRanges,
        filters,
        getCheckedValue,
        initialModelRanges,
        isLoadingModelRanges,
        isSuccessModelRanges,
        lastAction,
        onGoToNext: handleGoToNext,
        onGoToPrevious: handleGoToPrevious,
        onGoToStep: handleGoToStep,
        onSetCurrentStep: setCurrentStep,
        onSetFilter: handleSetFilter,
        refetch,
        steps,
        visibleStep
      }}
    >
      {children}
    </AdvisorContext.Provider>
  );
};

/**
 * Export `AdvisorProvider` provider.
 */

export default AdvisorProvider;
